// Environnements — pour chaque application : sa production et sa copie, allumees ou non, la
// version qui tourne de chaque cote, et ou partent ses sorties. LECTURE SEULE.
//
// Cadrage : docs/prompts/MASTER-FEAT-LA-CONSOLE-MONTRE-LES-ENVIRONNEMENTS-2026-10-29.md (temps 1 ;
// les boutons allumer / eteindre sont le temps 2, un autre lot).
//
// LA REGLE D AFFICHAGE : ce qui n a pas ete mesure s affiche « inconnu », avec sa raison, jamais
// « allume » ni en vert. Le serveur porte l etat ET sa raison ; la page n en fabrique aucun.
//
// @gs/ui tel quel (PageHeader, StatusBadge, QueryError).
import { useMemo } from 'react';
import type { TFunction } from 'i18next';
import { ArrowRight, Clock, Laptop, RefreshCw, Server, Shield, ShieldAlert, TestTube2 } from 'lucide-react';
import { ApiForbiddenError } from '@gs/api-client';
import { PageHeader, QueryError, StatusBadge } from '@gs/ui';
import type { StatusBadgeProps } from '@gs/ui';
import { useTranslation } from '../i18n';
import type { Motif } from '../hooks/useParcSante';
import {
  useEnvironnements,
  useRemesurerEnvironnements,
  type AppEnvironnements,
  type Cote,
  type EtatDeSortie,
  type EtatDuCote,
  type Raison,
  type Sortie,
} from '../hooks/useEnvironnements';
import { useRemesurerPassage } from '../hooks/usePassageDuSoir';
import ApprobationDuSoir from '../components/ApprobationDuSoir';

// ── Libelles : cles LITTERALES (une cle construite echappe a i18next-parser, qui la purge) ──

function libelleEtat(e: EtatDuCote, t: TFunction): string {
  switch (e) {
    case 'allume': return t('admin:env.etat.allume');
    case 'eteint': return t('admin:env.etat.eteint');
    case 'absent': return t('admin:env.etat.absent');
    case 'inconnu': return t('admin:env.etat.inconnu');
  }
}

const VARIANTE_ETAT: Record<EtatDuCote, StatusBadgeProps['variant']> = {
  allume: 'success',
  eteint: 'neutral',
  absent: 'neutral',
  inconnu: 'warning',
};

function libelleRaison(r: Raison, t: TFunction): string {
  switch (r) {
    case 'azure': return t('admin:env.raison.azure');
    case 'repond': return t('admin:env.raison.repond');
    case 'sans-droit-ni-reponse': return t('admin:env.raison.sansDroitNiReponse');
    case 'sans-adresse-ni-droit': return t('admin:env.raison.sansAdresseNiDroit');
    case 'emplacement-inattendu': return t('admin:env.raison.emplacementInattendu');
    case 'autre-application': return t('admin:env.raison.autreApplication');
  }
}

function libelleSortie(s: Sortie, t: TFunction): string {
  switch (s) {
    case 'courriel': return t('admin:env.sortie.courriel');
    case 'textos': return t('admin:env.sortie.textos');
    case 'fichiers': return t('admin:env.sortie.fichiers');
    case 'synchronisation': return t('admin:env.sortie.synchronisation');
  }
}

function libelleEtatDeSortie(e: EtatDeSortie, t: TFunction): string {
  switch (e) {
    case 'reelle': return t('admin:env.etatSortie.reelle');
    case 'coupee': return t('admin:env.etatSortie.coupee');
    case 'redirigee': return t('admin:env.etatSortie.redirigee');
    case 'inactive': return t('admin:env.etatSortie.inactive');
    case 'inconnu': return t('admin:env.etatSortie.inconnu');
  }
}

const VARIANTE_SORTIE: Record<EtatDeSortie, StatusBadgeProps['variant']> = {
  // Une sortie reelle n est pas une alerte en production : c est son role. Elle se dit en bleu.
  reelle: 'info',
  coupee: 'success',
  redirigee: 'success',
  inactive: 'neutral',
  inconnu: 'warning',
};

function libelleMotif(m: Motif, t: TFunction): string {
  switch (m.code) {
    case 'acces-refuse': return t('admin:parc.motif.accesRefuse', { http: m.http ?? 403 });
    case 'delai': return t('admin:parc.motif.delai');
    case 'configuration': return t('admin:parc.motif.configuration');
    case 'identite': return t('admin:parc.motif.identite');
    case 'reponse-invalide': return t('admin:parc.motif.reponseInvalide');
    case 'erreur-azure':
      return m.http ? t('admin:parc.motif.erreurAzure', { http: m.http }) : t('admin:parc.motif.erreurAzureSansCode');
  }
}

function libelleSonde(cote: Cote, copie: boolean, t: TFunction): string | null {
  const s = cote.sonde;
  if (s.etat === 'sans-adresse') return t('admin:env.sonde.sansAdresse');
  if (s.repond) return null;
  switch (s.erreur) {
    case 'delai': return t('admin:env.sonde.delai');
    case 'adresse-inconnue':
      // Pour une copie : ses deux lignes ne sont pas encore posees chez le registraire.
      return copie ? t('admin:env.sonde.adresseNonPosee') : t('admin:env.sonde.adresseInconnue');
    case 'http': return t('admin:env.sonde.http', { http: s.httpStatus ?? '—' });
    default: return t('admin:env.sonde.injoignable');
  }
}

function useFormats() {
  const { t, i18n } = useTranslation();
  const anglais = i18n.language?.startsWith('en') ?? false;
  return useMemo(() => {
    const heure = new Intl.DateTimeFormat(anglais ? 'en-CA' : 'fr-CA', { hour: 'numeric', minute: '2-digit' });
    const jourHeure = new Intl.DateTimeFormat(anglais ? 'en-CA' : 'fr-CA', {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
    });
    return {
      t,
      anglais,
      heure: (iso: string) => heure.format(new Date(iso)),
      jourHeure: (iso: string) => (Number.isNaN(Date.parse(iso)) ? iso : jourHeure.format(new Date(iso))),
    };
  }, [t, anglais]);
}
type Formats = ReturnType<typeof useFormats>;

// ── Le schema du parcours : poste -> copie -> production ────────────────────────────────

function Etape({ icone: Icone, titre, texte }: { icone: typeof Laptop; titre: string; texte: string }) {
  return (
    <div className="flex flex-1 items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-xs dark:border-slate-700 dark:bg-slate-800">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
        <Icone size={18} aria-hidden />
      </span>
      <div>
        <p className="text-body font-semibold text-slate-800 dark:text-slate-100">{titre}</p>
        <p className="mt-0.5 text-field text-slate-600 dark:text-slate-300">{texte}</p>
      </div>
    </div>
  );
}

function Fleche() {
  return (
    <div className="flex items-center justify-center text-slate-400 md:px-1" aria-hidden>
      <ArrowRight size={20} className="rotate-90 md:rotate-0" />
    </div>
  );
}

function Schema({ t }: { t: TFunction }) {
  return (
    <section aria-labelledby="titre-schema">
      <h2 id="titre-schema" className="text-head font-semibold text-slate-800 dark:text-slate-100">{t('admin:env.schema.titre')}</h2>
      <div className="mt-3 flex flex-col gap-2 md:flex-row md:items-stretch">
        <Etape icone={Laptop} titre={t('admin:env.schema.posteT')} texte={t('admin:env.schema.posteS')} />
        <Fleche />
        <Etape icone={TestTube2} titre={t('admin:env.schema.copieT')} texte={t('admin:env.schema.copieS')} />
        <Fleche />
        <Etape icone={Server} titre={t('admin:env.schema.productionT')} texte={t('admin:env.schema.productionS')} />
      </div>
      <p className="mt-2 text-meta text-slate-500 dark:text-slate-400">{t('admin:env.schema.aide')}</p>
    </section>
  );
}

// ── Un cote d une application ───────────────────────────────────────────────────────────

function BlocCote({ titre, cote, copie, f }: { titre: string; cote: Cote; copie: boolean; f: Formats }) {
  const { t } = f;
  const sonde = cote.sonde.etat === 'faite' && cote.sonde.repond ? cote.sonde : null;
  const pourquoiMuette = libelleSonde(cote, copie, t);
  return (
    <div className="flex-1 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-field font-semibold text-slate-700 dark:text-slate-200">{titre}</p>
        <StatusBadge variant={VARIANTE_ETAT[cote.etat]}>{libelleEtat(cote.etat, t)}</StatusBadge>
      </div>
      <p className="mt-1 text-meta text-slate-500 dark:text-slate-400">{libelleRaison(cote.raison, t)}</p>
      <dl className="mt-2 space-y-1 text-meta">
        <div className="flex gap-2">
          <dt className="text-slate-500 dark:text-slate-400">{t('admin:env.versionL')}</dt>
          <dd className="text-slate-800 dark:text-slate-100">
            {sonde?.version || sonde?.commit
              ? [sonde.version, sonde.commit ? sonde.commit.slice(0, 9) : null].filter(Boolean).join(' · ')
              : t('admin:env.inconnue')}
          </dd>
        </div>
        {sonde?.construitLe && (
          <div className="flex gap-2">
            <dt className="text-slate-500 dark:text-slate-400">{t('admin:env.construitL')}</dt>
            <dd className="text-slate-800 dark:text-slate-100">{f.jourHeure(sonde.construitLe)}</dd>
          </div>
        )}
        <div className="flex gap-2">
          <dt className="text-slate-500 dark:text-slate-400">{t('admin:env.adresseL')}</dt>
          <dd className="break-all text-slate-800 dark:text-slate-100">{cote.adresse ?? '—'}</dd>
        </div>
        {pourquoiMuette && (
          <div className="flex gap-2">
            <dt className="text-slate-500 dark:text-slate-400">{t('admin:env.reponseL')}</dt>
            <dd className="text-slate-800 dark:text-slate-100">{pourquoiMuette}</dd>
          </div>
        )}
        {cote.azure.etat === 'illisible' && (
          <div className="flex gap-2">
            <dt className="text-slate-500 dark:text-slate-400">{t('admin:env.azureL')}</dt>
            <dd className="text-slate-800 dark:text-slate-100">{libelleMotif(cote.azure.motif, t)}</dd>
          </div>
        )}
      </dl>
      <div className="mt-3">
        <p className="text-meta font-semibold text-slate-600 dark:text-slate-300">{t('admin:env.sortiesL')}</p>
        {cote.sorties.length === 0 ? (
          <p className="mt-1 text-meta text-slate-500 dark:text-slate-400">{t('admin:env.aucuneSortie')}</p>
        ) : (
          <ul className="mt-1 space-y-1">
            {cote.sorties.map((s) => (
              <li key={s.sortie} className="flex items-center justify-between gap-2 text-meta">
                <span className="text-slate-700 dark:text-slate-200">{libelleSortie(s.sortie, t)}</span>
                <StatusBadge variant={VARIANTE_SORTIE[s.etat]}>{libelleEtatDeSortie(s.etat, t)}</StatusBadge>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function libelleNiveau(n: AppEnvironnements['niveau'], t: TFunction): string {
  switch (n) {
    case 'poc': return t('admin:env.niveau.poc');
    case 'interne': return t('admin:env.niveau.interne');
    case 'terrain': return t('admin:env.niveau.terrain');
  }
}

function CarteApp({ app, f }: { app: AppEnvironnements; f: Formats }) {
  const { t } = f;
  return (
    <article
      aria-labelledby={`env-${app.cle}`}
      className="rounded-xl border border-slate-200 bg-white p-4 shadow-xs dark:border-slate-700 dark:bg-slate-800"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 id={`env-${app.cle}`} className="text-body font-semibold text-slate-800 dark:text-slate-100">
          {f.anglais ? app.nom.en : app.nom.fr}
        </h3>
        <StatusBadge variant="neutral">{libelleNiveau(app.niveau, t)}</StatusBadge>
      </div>
      <p className="mt-1 text-meta text-slate-500 dark:text-slate-400">
        {app.chemin === 'par-la-copie' ? t('admin:env.chemin.parLaCopie') : t('admin:env.chemin.direct')}
      </p>
      <div className="mt-3 flex flex-col gap-2 md:flex-row md:items-stretch">
        <BlocCote titre={t('admin:env.cote.copie')} cote={app.copie} copie f={f} />
        <Fleche />
        <BlocCote titre={t('admin:env.cote.production')} cote={app.production} copie={false} f={f} />
      </div>
    </article>
  );
}

// ── La page ─────────────────────────────────────────────────────────────────────────────

export default function EnvironnementsPage() {
  const f = useFormats();
  const { t } = f;
  const requete = useEnvironnements();
  const remesurer = useRemesurerEnvironnements();
  // « Remesurer » relit aussi le passage du soir : un seul bouton, deux lectures, aucune ecriture.
  const remesurerPassage = useRemesurerPassage();

  if (requete.isPending) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center gap-2 text-field text-slate-500">
        <RefreshCw size={16} className="animate-spin" aria-hidden />
        {t('admin:env.chargement')}
      </div>
    );
  }
  if (requete.error instanceof ApiForbiddenError) {
    return (
      <div className="mx-auto max-w-lg py-12 text-center">
        <Shield size={40} className="mx-auto mb-4 text-slate-300 dark:text-slate-600" aria-hidden />
        <p className="text-body font-semibold text-slate-800 dark:text-slate-100">{t('admin:env.refuseT')}</p>
        <p className="mt-1 text-field text-slate-500 dark:text-slate-400">{t('admin:env.refuseS')}</p>
      </div>
    );
  }
  if (requete.isError || !requete.data) {
    return <QueryError message={t('admin:env.erreur')} onRetry={() => void requete.refetch()} />;
  }

  const env = requete.data;
  const sansDroit = env.apps.some(
    (a) => a.copie.azure.etat === 'illisible' && a.copie.azure.motif.code === 'acces-refuse',
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('admin:env.titre')}
        description={t('admin:env.sousTitre', { n: env.apps.length })}
        actions={
          <>
            <span className="inline-flex items-center gap-1.5 text-meta text-slate-500 dark:text-slate-400">
              <Clock size={14} aria-hidden />
              {t('admin:env.mesure', { t: f.heure(env.mesureA) })}
            </span>
            <button
              type="button"
              onClick={() => {
                remesurer.mutate();
                remesurerPassage.mutate();
              }}
              disabled={remesurer.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-field font-semibold text-slate-800 touch-manipulation hover:bg-slate-200 disabled:cursor-progress disabled:opacity-60 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
            >
              <RefreshCw size={16} className={remesurer.isPending ? 'animate-spin' : ''} aria-hidden />
              {remesurer.isPending ? t('admin:env.remesureEnCours') : t('admin:env.remesurer')}
            </button>
          </>
        }
      />

      {sansDroit && (
        <div
          role="status"
          className="flex items-start gap-3 rounded-xl border border-l-3 border-slate-200 border-l-amber-500 bg-white px-3.5 py-3 shadow-xs dark:border-slate-700 dark:border-l-amber-400 dark:bg-slate-800"
        >
          <ShieldAlert size={18} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
          <div>
            <p className="text-field font-semibold text-slate-800 dark:text-slate-100">{t('admin:env.droit.titre')}</p>
            <p className="mt-0.5 text-meta text-slate-600 dark:text-slate-300">{t('admin:env.droit.texte')}</p>
          </div>
        </div>
      )}

      <ApprobationDuSoir jourHeure={f.jourHeure} />

      <Schema t={t} />

      <section aria-labelledby="titre-apps-env">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 id="titre-apps-env" className="text-head font-semibold text-slate-800 dark:text-slate-100">{t('admin:env.apps.titre')}</h2>
          <p className="text-meta text-slate-500 dark:text-slate-400">{t('admin:env.apps.aide')}</p>
        </div>
        <div className="mt-3 grid grid-cols-1 gap-4 xl:grid-cols-2">
          {env.apps.map((app) => (
            <CarteApp key={app.cle} app={app} f={f} />
          ))}
        </div>
      </section>
    </div>
  );
}
